/**
 * Multi-Organization Architecture Types
 * 
 * This file contains types for the Nexus multi-organization system
 * that enables resources to belong to multiple organizations simultaneously.
 * 
 * @version 1.0.0
 * @last-updated 2025-01-23
 * @package @flowstate/types
 */

// ============================================================================
// Core Multi-Organization Types
// ============================================================================

/**
 * Resource type identifiers for all entities that can be shared
 * across organizations
 */
export type ResourceType = 
  // Project Management
  | 'project' 
  | 'task' 
  | 'todo'
  // CRM & Sales Portal
  | 'client' 
  | 'opportunity' 
  | 'sales_transaction' 
  | 'sales_team' 
  | 'commission_rule'
  // Accounting Portal
  | 'invoice' 
  | 'expense' 
  | 'budget' 
  | 'subscription' 
  | 'transaction'
  // Community System
  | 'post' 
  | 'comment' 
  | 'community_group'
  // User Resources
  | 'user' 
  | 'profile';

/**
 * Access levels for shared resources
 */
export type AccessLevel = 'read' | 'write' | 'admin';

/**
 * Visibility scope determines how resources are shared
 */
export type VisibilityScope = 'private' | 'shared_read' | 'shared_write' | 'shared_admin';

/**
 * Relationship types between organizations
 */
export type RelationshipType = 'partner' | 'client' | 'vendor' | 'affiliate' | 'competitor' | 'internal';

/**
 * Organization size categories
 */
export type OrganizationSize = 'solo' | 'small' | 'medium' | 'large' | 'enterprise';

/**
 * Connection status for suggested connections
 */
export type ConnectionStatus = 'pending' | 'viewed' | 'accepted' | 'rejected';

/**
 * Core junction table entity linking resources to organizations
 */
export interface ResourceOrganization {
  id: string;
  resourceType: ResourceType;
  resourceId: string;
  organizationId: string;
  accessLevel: AccessLevel;
  isHomeOrg: boolean;
  visibilityScope: VisibilityScope;
  
  // Mycelium Protocol fields
  connectionStrength: number; // 0.00 - 1.00
  relationshipType: RelationshipType;
  lastAccessed: Date;
  interactionCount: number;
  
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Organization profile for discovery and matching
 */
export interface OrganizationProfile {
  organizationId: string;
  
  // Discovery metadata
  industryTags: string[];
  locationCountry?: string;
  locationCity?: string;
  sizeRange?: OrganizationSize;
  
  // Resource capabilities
  resourceCapabilities: Record<string, boolean | string | number>;
  resourceGaps: Record<string, boolean | string | number>;
  
  // Mycelial growth metrics
  discoveryScore: number;
  connectionCount: number;
  collaborationIndex: number;
  
  // Preferences
  sharingPreferences: Record<string, unknown>;
  autoAcceptPartners: boolean;
  
  updatedAt: Date;
}

/**
 * Suggested connection between organizations
 */
export interface SuggestedConnection {
  id: string;
  sourceOrgId: string;
  targetOrgId: string;
  
  // Scoring components
  sharedUsersScore: number;
  industryOverlapScore: number;
  proximityScore: number;
  complementaryScore: number;
  
  // Overall match
  totalScore: number;
  
  // Context
  suggestionReason: string[];
  potentialBenefits: string[];
  
  status: ConnectionStatus;
  createdAt: Date;
  expiresAt: Date;
}

/**
 * Audit trail for sharing changes
 */
export interface ResourceSharingAudit {
  id: string;
  resourceType: ResourceType;
  resourceId: string;
  action: 'shared' | 'unshared' | 'access_changed';
  organizationId?: string;
  oldAccessLevel?: AccessLevel;
  newAccessLevel?: AccessLevel;
  performedBy: string;
  performedAt: Date;
}

// ============================================================================
// Enhanced Organization Types
// ============================================================================

/**
 * Extended organization interface with multi-org features
 */
export interface MultiOrgOrganization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  subscriptionTier: string;
  isActive: boolean;
  
  // Multi-org additions
  profile?: OrganizationProfile;
  connectionStrength?: number; // For current user
  sharedResourcesCount?: number;
  userRole?: OrganizationRole;
  lastActivity?: Date;
  
  createdAt: Date;
  updatedAt: Date;
}

/**
 * User's relationship with an organization
 */
export interface UserOrganizationMembership {
  organization: MultiOrgOrganization;
  role: OrganizationRole;
  accessLevel: AccessLevel;
  isHome: boolean;
  joinedAt: Date;
}

/**
 * Organization roles
 */
export type OrganizationRole = 'owner' | 'admin' | 'member' | 'client';

// ============================================================================
// Portal-Specific Sharing Types
// ============================================================================

/**
 * Sales portal sharing options
 */
export interface SalesSharingOptions {
  allowCommissionSplit: boolean;
  territorySharing: boolean;
  leadDistribution: 'round-robin' | 'weighted' | 'manual';
  commissionPercentage?: number;
}

/**
 * Accounting portal sharing options
 */
export interface AccountingSharingOptions {
  costAllocationMethod: 'percentage' | 'fixed' | 'usage-based';
  taxJurisdiction: 'home_org' | 'shared' | 'per_org';
  currencyHandling: 'home_currency' | 'multi_currency';
  costPercentage?: number;
}

/**
 * Project sharing options
 */
export interface ProjectSharingOptions {
  inheritTaskPermissions: boolean;
  allowClientAccess: boolean;
  milestoneVisibility: 'public' | 'internal' | 'custom';
}

/**
 * Task sharing options
 */
export interface TaskSharingOptions {
  shareSubtasks: boolean;
  timeTrackingVisibility: 'public' | 'private';
  dependencyVisibility: boolean;
}

// ============================================================================
// Nexus Dashboard Types
// ============================================================================

/**
 * Organization card data for Nexus dashboard
 */
export interface OrganizationCard {
  organization: MultiOrgOrganization;
  userRole: OrganizationRole;
  memberCount: number;
  sharedResourcesCount: number;
  connectionStrength: number;
  isActive: boolean;
  lastActivity: Date;
  hasNotifications: boolean;
}

/**
 * Connection visualization data
 */
export interface OrganizationConnection {
  sourceOrgId: string;
  targetOrgId: string;
  strength: number;
  resourceFlow: {
    projects: number;
    tasks: number;
    clients: number;
  };
  lastInteraction: Date;
}

/**
 * Nexus dashboard filters
 */
export interface NexusFilters {
  organizationIds?: string[];
  resourceTypes?: ResourceType[];
  accessLevel?: AccessLevel;
  connectionStrength?: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

/**
 * Nexus dashboard view configuration
 */
export interface NexusViewConfig {
  showConnections: boolean;
  groupByOrganization: boolean;
  showSharedOnly: boolean;
  layout: 'grid' | 'list' | 'graph';
}

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Organization search filters
 */
export interface OrganizationSearchFilters {
  industry?: string;
  location?: string;
  sizeRange?: OrganizationSize;
  subscriptionTier?: string;
  isActive?: boolean;
}

/**
 * Share resource request
 */
export interface ShareResourceRequest {
  resourceType: ResourceType;
  resourceId: string;
  organizationId: string;
  accessLevel: AccessLevel;
  visibilityScope: VisibilityScope;
  relationshipType?: RelationshipType;
  portalOptions?: SalesSharingOptions | AccountingSharingOptions | ProjectSharingOptions | TaskSharingOptions | Record<string, unknown>;
}

/**
 * Bulk share request
 */
export interface BulkShareRequest {
  resourceType: ResourceType;
  resourceIds: string[];
  organizationIds: string[];
  accessLevel: AccessLevel;
  visibilityScope: VisibilityScope;
}

/**
 * Resource sharing response
 */
export interface ShareResourceResponse {
  success: boolean;
  resourceOrganization: ResourceOrganization;
  message?: string;
}

/**
 * Get accessible resources request
 */
export interface GetAccessibleResourcesRequest {
  resourceTypes?: ResourceType[];
  organizationIds?: string[];
  includeShared?: boolean;
  filters?: NexusFilters;
}

/**
 * Get accessible resources response
 */
export interface GetAccessibleResourcesResponse<T = Record<string, unknown>> {
  resources: Array<{
    resource: T;
    organizations: ResourceOrganization[];
    accessLevel: AccessLevel;
    isShared: boolean;
  }>;
  totalCount: number;
  hasMore: boolean;
}

// ============================================================================
// Service Interfaces
// ============================================================================

/**
 * Multi-organization service interface
 */
export interface IMultiOrgService {
  // Organization operations
  getUserOrganizations(userId: string): Promise<MultiOrgOrganization[]>;
  getOrganizationCards(userId: string): Promise<OrganizationCard[]>;
  getOrganizationConnections(userId: string): Promise<OrganizationConnection[]>;
  
  // Resource sharing
  shareResource(request: ShareResourceRequest): Promise<ShareResourceResponse>;
  unshareResource(resourceType: ResourceType, resourceId: string, organizationId: string): Promise<boolean>;
  updateResourceAccess(resourceType: ResourceType, resourceId: string, organizationId: string, accessLevel: AccessLevel): Promise<boolean>;
  
  // Bulk operations
  bulkShareResources(request: BulkShareRequest): Promise<ShareResourceResponse[]>;
  
  // Resource access
  getSharedResources(organizationId: string, resourceType?: ResourceType): Promise<ResourceOrganization[]>;
  getUserOwnedResources(userId: string, resourceType?: ResourceType): Promise<ResourceOrganization[]>;
  getAccessibleResources<T = Record<string, unknown>>(request: GetAccessibleResourcesRequest): Promise<GetAccessibleResourcesResponse<T>>;
  
  // Discovery
  getSuggestedConnections(organizationId: string): Promise<SuggestedConnection[]>;
  searchOrganizations(query: string, filters?: OrganizationSearchFilters): Promise<MultiOrgOrganization[]>;
  
  // Analytics
  getConnectionMetrics(organizationId: string): Promise<Record<string, unknown> | null>;
  getSharingAnalytics(resourceType: ResourceType, resourceId: string): Promise<Record<string, unknown> | null>;
}

// ============================================================================
// React Hook Types
// ============================================================================

/**
 * Hook return type for Nexus organizations
 */
export interface UseNexusOrganizationsReturn {
  organizations: OrganizationCard[];
  connections: OrganizationConnection[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook return type for accessible resources
 */
export interface UseAccessibleResourcesReturn<T = Record<string, unknown>> {
  resources: Array<{
    resource: T;
    organizations: ResourceOrganization[];
    accessLevel: AccessLevel;
    isShared: boolean;
  }>;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

/**
 * Hook return type for sharing operations
 */
export interface UseResourceSharingReturn {
  share: (request: ShareResourceRequest) => Promise<ShareResourceResponse>;
  unshare: (resourceType: ResourceType, resourceId: string, organizationId: string) => Promise<boolean>;
  updateAccess: (resourceType: ResourceType, resourceId: string, organizationId: string, accessLevel: AccessLevel) => Promise<boolean>;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Extract resource ID from union type
 */
export type ResourceId<T extends ResourceType> = T extends 'project' ? string
  : T extends 'task' ? string
  : T extends 'todo' ? string
  : T extends 'client' ? string
  : T extends 'opportunity' ? string
  : T extends 'sales_transaction' ? string
  : T extends 'sales_team' ? string
  : T extends 'commission_rule' ? string
  : T extends 'invoice' ? string
  : T extends 'expense' ? string
  : T extends 'budget' ? string
  : T extends 'subscription' ? string
  : T extends 'transaction' ? string
  : T extends 'post' ? string
  : T extends 'comment' ? string
  : T extends 'community_group' ? string
  : T extends 'user' ? string
  : T extends 'profile' ? string
  : string;

/**
 * Create a typed resource organization
 */
export type TypedResourceOrganization<T extends ResourceType> = ResourceOrganization & {
  resourceType: T;
};

/**
 * Sharing options by resource type
 */
export type SharingOptionsByResourceType = {
  project: ProjectSharingOptions;
  task: TaskSharingOptions;
  todo: TaskSharingOptions;
  client: SalesSharingOptions;
  opportunity: SalesSharingOptions;
  sales_transaction: SalesSharingOptions;
  invoice: AccountingSharingOptions;
  expense: AccountingSharingOptions;
  budget: AccountingSharingOptions;
  subscription: AccountingSharingOptions;
  transaction: AccountingSharingOptions;
  post: Record<string, unknown>;
  comment: Record<string, unknown>;
  community_group: Record<string, unknown>;
  user: Record<string, unknown>;
  profile: Record<string, unknown>;
};

// ============================================================================
// ZOD SCHEMAS — Validation for API boundaries
// ============================================================================

import { z } from 'zod';

export const ResourceTypeSchema = z.enum([
  'project', 'task', 'todo',
  'client', 'opportunity', 'sales_transaction', 'sales_team', 'commission_rule',
  'invoice', 'expense', 'budget', 'subscription', 'transaction',
  'post', 'comment', 'community_group',
  'user', 'profile',
]);

export const AccessLevelSchema = z.enum(['read', 'write', 'admin']);

export const VisibilityScopeSchema = z.enum([
  'private', 'shared_read', 'shared_write', 'shared_admin',
]);

export const RelationshipTypeSchema = z.enum([
  'partner', 'client', 'vendor', 'affiliate', 'competitor', 'internal',
]);

/**
 * Zod schema for ResourceOrganization — validates the junction table entity
 * before writing to the database or passing across API boundaries.
 */
export const ResourceOrganizationSchema = z.object({
  id: z.string().uuid(),
  resourceType: ResourceTypeSchema,
  resourceId: z.string().uuid(),
  organizationId: z.string().uuid(),
  accessLevel: AccessLevelSchema,
  isHomeOrg: z.boolean(),
  visibilityScope: VisibilityScopeSchema,
  connectionStrength: z.number().min(0).max(1),
  relationshipType: RelationshipTypeSchema,
  lastAccessed: z.coerce.date(),
  interactionCount: z.number().int().min(0),
  createdBy: z.string().uuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ShareResourceRequestSchema = z.object({
  resourceType: ResourceTypeSchema,
  resourceId: z.string().uuid(),
  organizationId: z.string().uuid(),
  accessLevel: AccessLevelSchema,
  visibilityScope: VisibilityScopeSchema,
  relationshipType: RelationshipTypeSchema.optional(),
});
